<template>
  <!-- Counts over time, one 2px line per series on a hairline grid. A single
       series gets an area wash; two or more get a legend that isolates a
       series when clicked. A crosshair finds the nearest bucket for the
       pointer or the arrow keys and reads every series there; the end of each
       line is labelled when the labels don't collide. The table view lists
       the same numbers, bucket by bucket. -->
  <div ref="root" class="lc" :class="{ 'is-loading': loading }" :aria-busy="loading ? 'true' : 'false'">
    <ul v-if="series.length > 1" class="lc-legend" aria-label="Series">
      <li v-for="s in series" :key="s.key">
        <button
          type="button"
          class="lc-legend__item"
          :class="{ 'is-off': isHidden(s.key) }"
          :aria-pressed="isHidden(s.key) ? 'false' : 'true'"
          :title="isHidden(s.key) ? `Show ${s.label}` : `Hide ${s.label}`"
          @click="toggle(s.key)"
        >
          <span class="lc-legend__key" :style="{ background: s.color }" aria-hidden="true"></span>
          <span class="lc-legend__label">{{ s.label }}</span>
          <span class="lc-legend__total">{{ format(totalOf(s)) }}</span>
        </button>
      </li>
    </ul>

    <div v-if="view === 'table'" class="chart-table-wrap">
      <table class="chart-table" :aria-label="label">
        <thead>
          <tr>
            <th scope="col">{{ periodLabel }}</th>
            <th v-for="s in series" :key="s.key" scope="col" class="is-num">{{ s.label }}</th>
            <th v-if="series.length > 1" scope="col" class="is-num">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(point, index) in points" :key="point.key">
            <th scope="row">{{ point.full }}</th>
            <td v-for="s in series" :key="s.key" class="is-num">{{ format(valueAt(s, index)) }}</td>
            <td v-if="series.length > 1" class="is-num">{{ format(sumAt(index)) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td v-for="s in series" :key="s.key" class="is-num">{{ format(totalOf(s)) }}</td>
            <td v-if="series.length > 1" class="is-num">{{ format(grandTotal) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div v-else class="lc-plot" :style="{ height: `${height}px` }" @pointerleave="onPointerLeave">
      <svg
        v-if="width > 0 && count > 0"
        ref="svg"
        class="lc-svg"
        :width="width"
        :height="height"
        :viewBox="`0 0 ${width} ${height}`"
        role="img"
        :aria-label="label"
      >
        <g class="lc-grid" aria-hidden="true">
          <line
            v-for="tick in yTicks"
            :key="`grid-${tick}`"
            :x1="plot.left"
            :x2="plot.right"
            :y1="yOf(tick)"
            :y2="yOf(tick)"
            :class="{ 'is-baseline': tick === 0 }"
          />
        </g>

        <g class="lc-axis" aria-hidden="true">
          <text v-for="tick in yTicks" :key="`y-${tick}`" :x="plot.left - 8" :y="yOf(tick)" text-anchor="end" dominant-baseline="middle">
            {{ format(tick) }}
          </text>
          <text v-for="item in xLabels" :key="`x-${item.index}`" :x="item.x" :y="height - 6" text-anchor="middle">
            {{ item.text }}
          </text>
        </g>

        <path v-if="areaPath" :d="areaPath" :fill="visible[0].color" fill-opacity="0.1" aria-hidden="true" />

        <g v-for="s in visible" :key="`line-${s.key}`" aria-hidden="true">
          <path
            v-if="pathOf(s)"
            :d="pathOf(s)"
            fill="none"
            :stroke="s.color"
            stroke-width="2"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <circle :cx="xOf(lastIndex)" :cy="yOf(valueAt(s, lastIndex))" r="4" :fill="s.color" class="lc-marker" />
        </g>

        <g class="lc-end" aria-hidden="true">
          <text v-for="end in endLabels" :key="`end-${end.key}`" :x="end.x" :y="end.y" text-anchor="end">{{ end.text }}</text>
        </g>

        <g v-if="hover !== null" aria-hidden="true">
          <line class="lc-crosshair" :x1="xOf(hover)" :x2="xOf(hover)" :y1="plot.top" :y2="plot.bottom" />
          <circle
            v-for="s in visible"
            :key="`hover-${s.key}`"
            :cx="xOf(hover)"
            :cy="yOf(valueAt(s, hover))"
            r="4"
            :fill="s.color"
            class="lc-marker"
          />
        </g>

        <!-- The hit layer: the crosshair finds the X, so a reader aims at a
             date, never at a 2px line. Focusable, for the arrow keys. -->
        <rect
          class="lc-hit"
          :x="plot.left"
          :y="plot.top"
          :width="plot.width"
          :height="plot.height"
          rx="4"
          fill="transparent"
          tabindex="0"
          role="group"
          :aria-label="`${label}. Use the arrow keys to move between ${periodLabel.toLowerCase()}s.`"
          @pointermove="onPointer"
          @pointerdown="onPointer"
          @keydown="onKey"
          @focus="onFocus"
          @blur="clearHover"
        />
      </svg>

      <div v-if="tip" class="lc-tip" :style="tip.style" aria-hidden="true">
        <p class="lc-tip__title">{{ tip.title }}</p>
        <ul class="lc-tip__rows">
          <li v-for="row in tip.rows" :key="row.key" class="lc-tip__row">
            <span class="lc-tip__key" :style="{ background: row.color }"></span>
            <span class="lc-tip__value">{{ row.value }}</span>
            <span class="lc-tip__label">{{ row.label }}</span>
          </li>
        </ul>
      </div>

      <p v-if="isEmpty && count > 0" class="lc-empty">{{ emptyText }}</p>
      <span class="sr-only" aria-live="polite">{{ announcement }}</span>
    </div>
  </div>
</template>
<script>
// Around the plot: room for the y labels, the end markers and the x labels.
const MARGIN = { top: 18, right: 22, bottom: 28, left: 44 };
// About how many gridlines to draw.
const Y_TICKS = 4;
// How the x labels are sized: pixels per character at 11px, and the gap kept between them.
const X_LABEL_CHAR = 6.4;
const X_LABEL_GAP = 12;
// End labels closer than this would overlap, so none are drawn.
const END_LABEL_MIN_GAP = 14;

const round1 = (value) => Math.round(value * 10) / 10;

// A round step for the y axis: 1, 2, 5 or 10 times a power of ten — never a
// fraction, since the counts are whole.
const niceStep = (max, count) => {
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => max / s <= count) || 10 * magnitude;
  return Math.max(1, Math.round(step * 100) / 100);
};

export default {
  name: 'LineChart',
  props: {
    // [{ key, at, short, full }] — the buckets, in order: how each is named on
    // the axis (short) and in the tooltip and table (full).
    points: { type: Array, required: true },
    // [{ key, label, color, values }] — values aligned to the points.
    series: { type: Array, required: true },
    // 'chart' or 'table'.
    view: { type: String, default: 'chart' },
    label: { type: String, default: 'Chart' },
    // What one bucket is: "Day", "Hour".
    periodLabel: { type: String, default: 'Day' },
    height: { type: Number, default: 280 },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: 'Nothing in this period' },
  },
  data() {
    return {
      width: 0,
      // Series switched off in the legend.
      hidden: [],
      // The bucket the crosshair is on, and what put it there.
      hover: null,
      hoverBy: null,
      observer: null,
    };
  },
  computed: {
    visible() {
      const shown = this.series.filter((s) => !this.hidden.includes(s.key));
      return shown.length ? shown : this.series;
    },
    count() {
      return this.points.length;
    },
    lastIndex() {
      return Math.max(0, this.count - 1);
    },
    plot() {
      const left = MARGIN.left;
      const top = MARGIN.top;
      const right = Math.max(left + 1, this.width - MARGIN.right);
      const bottom = Math.max(top + 1, this.height - MARGIN.bottom);
      return { left, top, right, bottom, width: right - left, height: bottom - top };
    },
    step() {
      return this.count > 1 ? this.plot.width / (this.count - 1) : 0;
    },
    maxValue() {
      let max = 0;
      this.visible.forEach((s) => {
        this.points.forEach((point, index) => {
          max = Math.max(max, this.valueAt(s, index));
        });
      });
      return max;
    },
    yTicks() {
      const size = niceStep(Math.max(this.maxValue, 1), Y_TICKS);
      const top = Math.max(size, Math.ceil(this.maxValue / size) * size);
      const ticks = [];
      for (let value = 0; value <= top + size / 2; value += size) ticks.push(round1(value));
      return ticks;
    },
    yMax() {
      return this.yTicks[this.yTicks.length - 1] || 1;
    },
    // Which buckets get an axis label: the last one always, then every so many
    // back from it that the labels fit, kept inside the plot's edges.
    xLabels() {
      if (!this.count || this.width <= 0) return [];
      const widthOf = (text) => String(text).length * X_LABEL_CHAR;
      if (this.count === 1) {
        return [{ index: 0, x: round1(this.xOf(0)), text: this.points[0].short }];
      }
      const widest = Math.max(...this.points.map((point) => widthOf(point.short)));
      const every = Math.max(1, Math.ceil((widest + X_LABEL_GAP) / this.step));
      const placed = [];
      for (let index = this.lastIndex; index >= 0; index -= every) {
        const text = this.points[index].short;
        const half = widthOf(text) / 2;
        const x = Math.min(Math.max(this.xOf(index), this.plot.left + half), this.plot.right - half);
        placed.push({ index, x: round1(x), text, left: x - half, right: x + half });
      }
      // Clamping can push a label into its neighbour: the later one wins.
      const kept = [];
      placed.forEach((item) => {
        const previous = kept[kept.length - 1];
        if (!previous || item.right + X_LABEL_GAP / 2 <= previous.left) kept.push(item);
      });
      return kept.reverse();
    },
    // One series on its own gets a wash under its line.
    areaPath() {
      if (this.visible.length !== 1 || this.count < 2) return '';
      const line = this.pathOf(this.visible[0]);
      const base = round1(this.plot.bottom);
      return `${line} L${round1(this.xOf(this.lastIndex))} ${base} L${round1(this.xOf(0))} ${base} Z`;
    },
    // The value at the end of each line — except where lines end so close
    // together that their labels would overlap; there the legend and the
    // tooltip carry the values.
    endLabels() {
      if (this.isEmpty || !this.count) return [];
      const labels = this.visible.map((s) => {
        const value = this.valueAt(s, this.lastIndex);
        return {
          key: s.key,
          x: round1(this.xOf(this.lastIndex) + 6),
          y: round1(Math.max(this.plot.top - 4, this.yOf(value) - 11)),
          text: this.format(value),
        };
      });
      const byY = [...labels].sort((a, b) => a.y - b.y);
      const crowded = new Set();
      for (let i = 1; i < byY.length; i += 1) {
        if (byY[i].y - byY[i - 1].y < END_LABEL_MIN_GAP) {
          crowded.add(byY[i].key);
          crowded.add(byY[i - 1].key);
        }
      }
      return labels.filter((item) => !crowded.has(item.key));
    },
    isEmpty() {
      return this.maxValue === 0;
    },
    grandTotal() {
      return this.series.reduce((sum, s) => sum + this.totalOf(s), 0);
    },
    tip() {
      if (this.hover === null || !this.points[this.hover] || this.width <= 0) return null;
      const x = this.xOf(this.hover);
      const rows = this.visible.map((s) => ({
        key: s.key,
        color: s.color,
        value: this.format(this.valueAt(s, this.hover)),
        label: s.label,
      }));
      if (this.visible.length > 1) {
        rows.push({ key: '__total', color: 'transparent', value: this.format(this.sumVisibleAt(this.hover)), label: 'Total' });
      }
      // Beside the crosshair, on whichever side has the room.
      const style = x > this.width / 2
        ? { right: `${round1(this.width - x + 14)}px`, top: `${this.plot.top}px` }
        : { left: `${round1(x + 14)}px`, top: `${this.plot.top}px` };
      return { title: this.points[this.hover].full, rows, style };
    },
    announcement() {
      if (!this.tip) return '';
      return `${this.tip.title}: ${this.tip.rows.map((row) => `${row.label} ${row.value}`).join(', ')}`;
    },
  },
  watch: {
    // A new period: the crosshair starts over.
    points() {
      this.hover = null;
    },
    // Series that are gone can't stay hidden.
    series(next) {
      const keys = next.map((s) => s.key);
      this.hidden = this.hidden.filter((key) => keys.includes(key));
    },
  },
  methods: {
    format(value) {
      return (Number(value) || 0).toLocaleString('en-US');
    },
    valueAt(s, index) {
      const value = Array.isArray(s.values) ? s.values[index] : 0;
      return Math.max(0, Number(value) || 0);
    },
    totalOf(s) {
      return this.points.reduce((sum, point, index) => sum + this.valueAt(s, index), 0);
    },
    sumAt(index) {
      return this.series.reduce((sum, s) => sum + this.valueAt(s, index), 0);
    },
    sumVisibleAt(index) {
      return this.visible.reduce((sum, s) => sum + this.valueAt(s, index), 0);
    },
    xOf(index) {
      return this.count > 1 ? this.plot.left + index * this.step : this.plot.left + this.plot.width / 2;
    },
    yOf(value) {
      return this.plot.bottom - (Math.max(0, value) / this.yMax) * this.plot.height;
    },
    pathOf(s) {
      if (this.count < 2) return '';
      return this.points
        .map((point, index) => `${index ? 'L' : 'M'}${round1(this.xOf(index))} ${round1(this.yOf(this.valueAt(s, index)))}`)
        .join(' ');
    },
    isHidden(key) {
      return this.hidden.includes(key);
    },
    // Switches a series off, or on; the last one showing brings the rest back.
    toggle(key) {
      const shown = this.series.filter((s) => !this.hidden.includes(s.key));
      if (shown.length === 1 && shown[0].key === key) {
        this.hidden = [];
        return;
      }
      this.hidden = this.hidden.includes(key) ? this.hidden.filter((k) => k !== key) : [...this.hidden, key];
    },
    // The bucket nearest the pointer. The pointer's position is in rendered
    // pixels and the plot's in the SVG's own, which differ under the app's
    // zoom (or any transform), so it is scaled by the SVG's rendered width.
    indexAt(clientX) {
      const svg = this.$refs.svg;
      if (!svg || !this.count) return null;
      if (this.count === 1) return 0;
      const rect = svg.getBoundingClientRect();
      const scale = rect.width > 0 ? this.width / rect.width : 1;
      const x = (clientX - rect.left) * scale;
      return Math.min(this.lastIndex, Math.max(0, Math.round((x - this.plot.left) / this.step)));
    },
    onPointer(event) {
      const index = this.indexAt(event.clientX);
      if (index === null) return;
      this.hover = index;
      this.hoverBy = 'pointer';
    },
    onPointerLeave() {
      if (this.hoverBy === 'pointer') this.clearHover();
    },
    onFocus() {
      if (this.hover === null && this.count) {
        this.hover = this.lastIndex;
        this.hoverBy = 'keyboard';
      }
    },
    onKey(event) {
      if (event.key === 'Escape') {
        this.clearHover();
        return;
      }
      const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowDown: -1, ArrowUp: 1 };
      let next = null;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.lastIndex;
      else if (event.key in moves) next = (this.hover === null ? this.lastIndex : this.hover) + moves[event.key];
      if (next === null) return;
      event.preventDefault();
      this.hover = Math.min(this.lastIndex, Math.max(0, next));
      this.hoverBy = 'keyboard';
    },
    clearHover() {
      this.hover = null;
      this.hoverBy = null;
    },
    measure() {
      const root = this.$refs.root;
      this.width = root ? Math.round(root.clientWidth) : 0;
    },
  },
  mounted() {
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.$refs.root);
    } else {
      window.addEventListener('resize', this.measure);
    }
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.measure);
  },
};
</script>
<style scoped>
.lc {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  transition: opacity .15s ease;
}

.lc.is-loading {
  opacity: 0.6;
}

/* ------------------------------- legend ------------------------------- */
.lc-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.lc-legend li {
  list-style: none;
}

.lc-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 4px 11px;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-pill);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  font: inherit;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, opacity .15s ease;
}

.lc-legend__item:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong) !important;
}

.lc-legend__item:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus) !important;
}

.lc-legend__item.is-off {
  opacity: 0.55;
}

.lc-legend__item.is-off .lc-legend__key {
  background: var(--color-border-strong) !important;
}

/* The key mirrors the mark: a short line. */
.lc-legend__key {
  display: inline-block;
  width: 14px;
  height: 2px;
  border-radius: 1px;
}

.lc-legend__label {
  color: var(--color-text-primary);
}

.lc-legend__total {
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
}

/* -------------------------------- plot -------------------------------- */
.lc-plot {
  position: relative;
  width: 100%;
  min-width: 0;
}

.lc-svg {
  display: block;
  overflow: visible;
}

.lc-grid line {
  stroke: var(--color-divider);
  stroke-width: 1;
  shape-rendering: crispEdges;
}

.lc-grid line.is-baseline {
  stroke: var(--color-border-strong);
}

.lc-axis text {
  fill: var(--color-text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

/* A ring in the surface colour keeps a marker legible where lines cross. */
.lc-marker {
  stroke: var(--color-bg-canvas);
  stroke-width: 2;
}

.lc-end text {
  fill: var(--color-text-secondary);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.lc-crosshair {
  stroke: var(--color-border-strong);
  stroke-width: 1;
  shape-rendering: crispEdges;
}

.lc-hit {
  cursor: crosshair;
  outline: none;
}

.lc-hit:focus-visible {
  stroke: var(--color-primary);
  stroke-width: 2;
}

/* ------------------------------- tooltip ------------------------------- */
.lc-tip {
  position: absolute;
  z-index: 2;
  min-width: 150px;
  max-width: 260px;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  box-shadow: var(--shadow-md);
  pointer-events: none;
}

.lc-tip__title {
  margin: 0 0 6px;
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  font-weight: 600;
  white-space: nowrap;
}

.lc-tip__rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.lc-tip__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lc-tip__key {
  flex: none;
  width: 12px;
  height: 2px;
  border-radius: 1px;
}

/* The value leads: the reader has the series and wants the number. */
.lc-tip__value {
  color: var(--color-text-primary);
  font-size: var(--fs-sm);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.lc-tip__label {
  color: var(--color-text-secondary);
  font-size: var(--fs-xs);
}

.lc-empty {
  position: absolute;
  top: 45%;
  left: 50%;
  margin: 0;
  padding: 6px 12px;
  border-radius: var(--radius-pill);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

/* ------------------------------ table view ------------------------------ */
.chart-table-wrap {
  overflow: auto;
  max-height: 360px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.chart-table {
  width: 100%;
  margin: 0;
  border-collapse: collapse;
  font-size: var(--fs-sm);
}

.chart-table th,
.chart-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-divider);
  color: var(--color-text-primary);
  text-align: left;
  vertical-align: top;
  white-space: nowrap;
}

.chart-table thead th {
  position: sticky;
  top: 0;
  background: var(--color-bg-soft);
  color: var(--color-text-muted);
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.chart-table tbody th {
  font-weight: 500;
}

.chart-table tfoot th,
.chart-table tfoot td {
  border-bottom: 0;
  background: var(--color-bg-soft);
  font-weight: 700;
}

.chart-table .is-num {
  font-variant-numeric: tabular-nums;
  text-align: right;
}
</style>
